// This file contains the sagas used for async actions in our app. It's divided into
// "effects" that the sagas call (`authorize` and `logout`) and the actual sagas themselves,
// which listen for actions.

// Sagas help us gather all our side effects (network requests in this case) in one place

import { browserHistory } from 'react-router';
import { delay } from 'redux-saga';
import { take, call, put, fork, race, all } from 'redux-saga/effects';
import { change, reset } from 'redux-form';
import store from 'store';
import moment from 'moment';
import _filter from 'lodash/filter';
import _orderBy from 'lodash/orderBy';

import { updateBusiness } from 'mf-shared/api/business';
import * as actionNotification from '../actions/notifications';
import * as action from '../actions';
import * as actionRegister from '../actions/registration';
import * as actionBusiness from '../actions/business';
import * as forgotPasswordAction from '../actions/forgotPassword';
import {
  LOGIN_REQUEST,
  LOGOUT,
  RESEND_VERIFICATION_EMAIL,
  BUSINESS_TYPES_REQUEST,
  BUSINESS_CATEGORIES_ALL_REQUEST,
  BUSINESS_STARTS_REQUEST,
  BUSINESS_LOCATIONS_REQUEST,
  BUSINESS_CITIES_ALL_REQUEST,
  BUSINESS_CITIES_POPULAR_REQUEST,
  BUSINESS_PROVINCE_REQUEST,
  BUSINESS_CITY_REQUEST,
  BUSINESS_ZIPCODE_REQUEST,
  GET_DROPDOWN_REQUEST,
  BUSINESS_KECAMATAN_REQUEST,
  BANK_LIST_REQUEST,
  BUSINESS_SALES_REQUEST,
  REDIRECT_TO,
  LOAD_RESET_PASSWORD,
  SEND_RESET_PASSWORD,
  LOAD_PREPARE_ENCRYPTION,
  GET_PROFILE_REQUEST,
  TO_LOGIN_PAGE,
  BUSINESS_OPENINGS_REQUEST,
  BUSINESS_NUMBER_OF_OUTLETS_REQUEST,
  BUSINESS_INFO_DATA_REQUEST,
} from '../actions/constants';
import { SET_BUSINESS_INFORMATION } from '../actions/registration/constants';
import {
  isSubscriptionExpired,
  showSubscriptionExpiredModal,
} from '../actions/subscriptionExpired';

import * as auth from '../api/auth';
import { getProfile } from '../api/profile';
import { isExternalRedirect } from '../utils/sso';
import { invalidateSession } from '../utils/auth';
import {
  assertErrorHasMessage,
  assertErrorHasResponse,
  assertErrorHasData,
} from '../utils/assert';
import * as clevertapRegister from '../clevertap/register';
import * as clevertapLogin from '../clevertap/login';
import * as clevertapLogout from '../clevertap/logout';
import { getProfile as apiGetProfile } from './api/profile';

// Little helper function to abstract going to different pages
function forwardTo(location) {
  browserHistory.push(location);
}

/**
 * Effect to handle authorization
 * @param  {string} email               The email of the user
 * @param  {string} password               The password of the user
 * @param  {object} options                Options
 * @param  {boolean} options.isRegistering Is this a register request?
 */
export function* authorize(email, password, phone) {
  // We send an action that tells Redux we're sending a request
  // We then try to register or log in the user, depending on the request
  try {
    const response = yield call(auth.login, email, password, phone);
    return response.data;
  } catch (error) {
    assertErrorHasResponse(error);
    // If we get an error we send Redux the appropiate action and return
    if (error.response) {
      const { data } = error.response;
      if (data.error.user_message) {
        return { error: data.error.user_message, code: data.error.code };
      }
      assertErrorHasMessage(error);
      return { error: error.message, code: data.error.code };
    }
    assertErrorHasMessage(error);
    return { error: error.message };
  }
}

/**
 * Effect to handle logging out
 */
export function* logout() {
  // We tell Redux we're in the middle of a request
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  // Similar to above, we try to log out by calling the `logout` function in the
  // `auth` module. If we get an error, we send an appropiate action. If we don't,
  // we return the response.
  try {
    yield call(auth.logout);
  } catch (error) {
    assertErrorHasMessage(error);
    const eobj = new Error(error.message);
    yield put(action.requestError(eobj));
  } finally {
    invalidateSession();
    sessionStorage.removeItem('welcomeBannerDisplayed');
    yield put(action.sendingRequest(false));
    yield put(action.hideProgressBar());
  }
}

/**
 * Log in saga
 */
export function* loginFlow() {
  // Because sagas are generators, doing `while (true)` doesn't block our program
  // Basically here we say "this saga is always listening for actions"
  while (true) {
    // And we're listening for `LOGIN_REQUEST` actions and destructuring its payload
    const request = yield take(LOGIN_REQUEST);
    const {
      email,
      password,
      phone,
      businessTypesState,
      businessCategoriesAllState,
    } = request.payload.data;
    const { next } = request.payload;
    yield put(action.sendingRequest(true));
    yield put(action.showProgressBar());
    // A `LOGOUT` action may happen while the `authorize` effect is going on, which may
    // lead to a race condition. This is unlikely, but just in case, we call `race` which
    // returns the "winner", i.e. the one that finished first
    const winner = yield race({
      auth: call(authorize, email, password, phone),
      logout: take(LOGOUT),
    });

    let businessTypes;
    let businessCategories;
    if (
      businessTypesState.length === 0 ||
      businessCategoriesAllState.length === 0
    ) {
      const [{ data: businessTypesTemp }, { data: businessCategoriesTemp }] =
        yield all([
          call(auth.getBusinessTypes),
          call(auth.getBusinessCategoriesAll),
        ]);
      yield put(action.changeBusinessTypesData(businessTypesTemp));
      yield put(action.changeBusinessCategoriesAllData(businessCategoriesTemp));
      businessTypes = businessTypesTemp;
      businessCategories = businessCategoriesTemp;
    } else {
      businessTypes = businessTypesState;
      businessCategories = businessCategoriesAllState;
    }

    yield put(action.sendingRequest(false));
    yield put(action.hideProgressBar());
    if (winner.auth) {
      const {
        errorTypes,
        user,
        error,
        permissions,
        role,
        access_token: accessToken,
      } = winner.auth;
      if (user) {
        store.set(`response:user_role_name`, role.name);
        // user is logged in
        const profile = yield call(getProfile);
        const { data } = profile;
        try {
          clevertapLogin.setUserProfile({
            profile: data,
            businessTypes,
            businessCategories,
          });
          clevertapLogin.login({ profile: data, role });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err);
        }
        yield put(action.setPermissionsState(permissions));
        yield put(action.setAuthState(true, data));
        let isRegistrationComplete = data.business.is_registration_complete;
        // we used isRegistrationComplete === null for handle old data.
        // currently need to handle 3 version of business data. 1. when is_registration_complete == null
        // 2. is_registration_complete == false, 3. is_registration_complete == true
        if (isRegistrationComplete === null) {
          isRegistrationComplete = true;
        }
        yield put(
          actionBusiness.setModalCompleteBusinessProfile(
            !isRegistrationComplete
          )
        );
        if (next && isExternalRedirect(next)) {
          forwardTo('/');
        }

        if (user.is_owner || user.is_manager) {
          if (moment(profile.data.business.expired_at) <= moment()) {
            yield put(isSubscriptionExpired(true));
            yield put(showSubscriptionExpiredModal());
            forwardTo('/settings/billing');
          } else {
            // owner or manager go to dashboard page,
            // unless next url is provided
            // if next url is a forbidden page
            // the interceptor should be given in react-router, not here
            if (next && isExternalRedirect(next)) {
              forwardTo('/');
            } else if (next === 'gostore-merchant') {
              window.location.href = `${process.env.GOSTORE_AUTH_URL}?token=${accessToken}`;
            } else if (next) {
              forwardTo(next);
            } else {
              // backend should return at least one, which is account setting
              const backofficePermissions = _filter(permissions, {
                access: 'Back-office',
              });
              const firstSubMenu = _orderBy(
                backofficePermissions,
                'priority_rank'
              )[0].urls[0];
              forwardTo(firstSubMenu);
            }
            yield put(
              actionNotification.success(`Welcome back, ${user.first_name}`)
            );
          }
        } else {
          if (next && isExternalRedirect(next)) {
            forwardTo('/');
          } else {
            // staff go to account settings
            const backofficePermissions = _filter(permissions, {
              access: 'Back-office',
            });
            const firstSubMenu = _orderBy(
              backofficePermissions,
              'priority_rank'
            )[0].urls[0];
            forwardTo(firstSubMenu);
          }
          yield put(
            actionNotification.success(`Welcome back, ${user.first_name}`)
          );
        }
      } else if (error) {
        // user can't loging because have wrong password / email
        yield call(delay, 1000);
        yield put(actionNotification.error(error));
      } else if (errorTypes) {
        // user has logined
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } else if (winner.logout) {
      yield call(logout);
      forwardTo('/login');
    }
  }
}

export function* resendVerificationEmailFlow() {
  while (true) {
    const request = yield take(RESEND_VERIFICATION_EMAIL);
    const { newEmail, captcha, resetCaptcha } = request.payload.data;
    let { oldEmail } = request.payload.data;
    // UI
    yield put(action.sendingRequest(true));
    yield put(action.showProgressBar());
    try {
      // api call
      const response = yield call(auth.resendVerificationEmail, {
        oldEmail,
        newEmail,
        captcha,
      });
      if (response.status === 200) {
        oldEmail = newEmail;
      }
      clevertapRegister.resendVerificationEmail({ email: newEmail });
      yield put(actionNotification.success(response.data.message));
    } catch (error) {
      assertErrorHasMessage(error);
      yield put(actionNotification.error(error.message));
    } finally {
      yield put(actionRegister.resetCaptcha(resetCaptcha + 1));
      yield put(reset('confirmEmailModalForm'));
      yield put(change('confirmEmailModalForm', 'oldEmailAddress', oldEmail));
      yield put(change('confirmEmailModalForm', 'newEmailAddress', newEmail));
      yield put(change('confirmEmailModalForm', 'chapcha', ''));
      yield put(action.hideProgressBar());
      yield put(action.sendingRequest(false));
    }
  }
}

/**
 * Log out saga
 * This is basically the same as the `if (winner.logout)` of above, just written
 * as a saga that is always listening to `LOGOUT` actions
 */
export function* logoutFlow() {
  while (true) {
    yield take(LOGOUT);
    const profile = yield call(getProfile);
    const roleName = store.get('response:user_role_name');
    try {
      clevertapLogout.logout({ data: profile.data, roleName });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
    yield call(logout);
    // don't remove window.location.replace, because this code is temporary fix.
    window.location.replace('/login');
  }
}

export function* fetchBusinessTypes() {
  yield put(action.sendingRequest(true));
  try {
    const response = yield call(auth.getBusinessTypes);
    return response.data;
  } catch (error) {
    yield put(action.requestError(error));
  } finally {
    yield put(action.sendingRequest(false));
  }
}

export function* businessTypesFlow() {
  while (true) {
    yield take(BUSINESS_TYPES_REQUEST);

    const response = yield call(fetchBusinessTypes);
    yield put(action.changeBusinessTypesData(response));
  }
}

export function* fetchBusinessCategories(businessTypeId) {
  yield put(action.sendingRequest(true));
  try {
    const response = yield call(auth.getBusinessCategories, businessTypeId);
    return response.data;
  } catch (error) {
    yield put(action.requestError(error));
  } finally {
    yield put(action.sendingRequest(false));
  }
}

export function* businessCategoriesFlow() {
  while (true) {
    const request = yield take('BUSINESS_CATEGORIES_REQUEST');
    const businessTypeId = request.payload.data;
    const response = yield call(fetchBusinessCategories, businessTypeId);
    yield put(action.changeBusinessCategoriesData(response));
  }
}

export function* fetchBusinessCategoriesAll() {
  yield put(action.sendingRequest(true));
  try {
    const response = yield call(auth.getBusinessCategoriesAll);
    return response.data;
  } catch (error) {
    yield put(action.requestError(error));
  } finally {
    yield put(action.sendingRequest(false));
  }
}

export function* businessCategoriesAllFlow() {
  while (true) {
    yield take(BUSINESS_CATEGORIES_ALL_REQUEST);
    const response = yield call(fetchBusinessCategoriesAll);
    yield put(action.changeBusinessCategoriesAllData(response));
  }
}

export function* fetchBusinessOpeningChoices() {
  yield put(action.sendingRequest(true));
  try {
    const response = yield call(auth.getBusinessOpeningChoices);
    return response.data.data;
  } catch (error) {
    yield put(action.requestError(error));
  } finally {
    yield put(action.sendingRequest(false));
  }
}

export function* businessOpeningChoicesFlow() {
  while (true) {
    yield take(BUSINESS_OPENINGS_REQUEST);
    const response = yield call(fetchBusinessOpeningChoices);
    yield put(action.changeBusinessOpeningChoices(response));
  }
}

export function* fetchBusinessNumberOfOutletsChoices() {
  yield put(action.sendingRequest(true));
  try {
    const response = yield call(auth.getBusinessNumberOfOutletsChoices);
    return response.data.data;
  } catch (error) {
    yield put(action.requestError(error));
  } finally {
    yield put(action.sendingRequest(false));
  }
}

export function* businessNumberOfOutletsFlow() {
  while (true) {
    yield take(BUSINESS_NUMBER_OF_OUTLETS_REQUEST);
    const response = yield call(fetchBusinessNumberOfOutletsChoices);
    yield put(action.businessNumberOfOutletsChoicesData(response));
  }
}

export function* fetchBusinessInformation() {
  yield put(action.sendingRequest(true));
  try {
    const response = yield call(auth.getBusinessInfo);
    return response.data.data;
  } catch (error) {
    yield put(action.requestError(error));
  } finally {
    yield put(action.sendingRequest(false));
  }
}

export function* businessInformationFlow() {
  while (true) {
    yield take(BUSINESS_INFO_DATA_REQUEST);
    const response = yield call(fetchBusinessInformation);
    yield put(action.setBusinessInformationData(response));
  }
}

export function* businessStartsFlow() {
  while (true) {
    yield take(BUSINESS_STARTS_REQUEST);
    const currentYear = new Date().getFullYear();
    const yearState: any[] = [];
    for (let i = 0; i <= 30; i++) {
      yearState.push({
        id: currentYear - i,
        name: currentYear - i,
      });
    }
    yield put(action.changeBusinessStartsData(yearState));
  }
}

export function* fetchBusinessLocations() {
  yield put(action.sendingRequest(true));
  try {
    const response = yield call(auth.getBusinessLocations);
    return response.data;
  } catch (error) {
    yield put(action.requestError(error));
  } finally {
    yield put(action.sendingRequest(false));
  }
}

export function* businessLocationsFlow() {
  while (true) {
    yield take(BUSINESS_LOCATIONS_REQUEST);
    const response = yield call(fetchBusinessLocations);
    yield put(action.changeBusinessLocationsData(response));
  }
}

export function* fetchBusinessProvince() {
  yield put(action.sendingRequest(true));
  try {
    const response = yield call(auth.getBusinessProvince);
    return response.data;
  } catch (error) {
    yield put(action.requestError(error));
  } finally {
    yield put(action.sendingRequest(false));
  }
}

export function* businessProvinceFlow() {
  while (true) {
    yield take(BUSINESS_PROVINCE_REQUEST);
    const response = yield call(fetchBusinessProvince);
    const newProvinceList = {};
    for (const item of response) {
      newProvinceList[item.name] = item.id;
    }
    yield put(action.changeBusinessProvinceData(newProvinceList));
  }
}

export function* fetchBusinessCity(provinceId) {
  yield put(action.sendingRequest(true));
  try {
    const response = yield call(auth.getBusinessCity, provinceId);
    return response.data;
  } catch (error) {
    yield put(action.requestError(error));
  } finally {
    yield put(action.sendingRequest(false));
  }
}

export function* businessCityFlow() {
  while (true) {
    const request = yield take(BUSINESS_CITY_REQUEST);
    const { provinceId } = request.payload;
    const response = yield call(fetchBusinessCity, provinceId);
    const newCityList = {};
    for (const item of response) {
      newCityList[item.name] = item.id;
    }
    yield put(action.changeBusinessCityData(newCityList));
  }
}

export function* fetchBusinessCitiesAll() {
  yield put(action.sendingRequest(true));
  try {
    const response = yield call(auth.getBusinessCitiesAll);
    return response.data.data;
  } catch (error) {
    yield put(action.requestError(error));
  } finally {
    yield put(action.sendingRequest(false));
  }
}

export function* businessCitiesAllFlow() {
  while (true) {
    yield take(BUSINESS_CITIES_ALL_REQUEST);
    const response = yield call(fetchBusinessCitiesAll);
    yield put(action.businessCitiesAllData(response));
  }
}

export function* fetchBusinessCitiesPopular() {
  yield put(action.sendingRequest(true));
  try {
    const response = yield call(auth.getBusinessCitiesPopular);
    return response.data.data;
  } catch (error) {
    yield put(action.requestError(error));
  } finally {
    yield put(action.sendingRequest(false));
  }
}

export function* businessCitiesPopularFlow() {
  while (true) {
    yield take(BUSINESS_CITIES_POPULAR_REQUEST);
    const response = yield call(fetchBusinessCitiesPopular);
    yield put(action.businessCitiesPopularData(response));
  }
}

export function* fetchBusinessZipcode(kecamatanId) {
  yield put(action.sendingRequest(true));
  try {
    const response = yield call(auth.getBusinessZipcode, kecamatanId);
    return response.data;
  } catch (error) {
    yield put(action.requestError(error));
  } finally {
    yield put(action.sendingRequest(false));
  }
}

export function* businessZipcodeFlow() {
  while (true) {
    const request = yield take(BUSINESS_ZIPCODE_REQUEST);
    const { kecamatanId } = request.payload;
    const response = yield call(fetchBusinessZipcode, kecamatanId);
    yield put(action.changeBusinessZipcodeData(response));
  }
}

export function* fetchBusinessKecamatan(cityId) {
  yield put(action.sendingRequest(true));
  try {
    const response = yield call(auth.getBusinessKecamatan, cityId);
    return response.data;
  } catch (error) {
    yield put(action.requestError(error));
  } finally {
    yield put(action.sendingRequest(false));
  }
}

export function* businessKecamatanFlow() {
  while (true) {
    const request = yield take(BUSINESS_KECAMATAN_REQUEST);
    const { cityId } = request.payload;
    const response = yield call(fetchBusinessKecamatan, cityId);
    const newKecamatanList = {};
    for (const item of response) {
      newKecamatanList[item.name] = item.id;
    }
    yield put(action.changeBusinessKecamatanData(newKecamatanList));
  }
}

export function* fetchBankList() {
  yield put(action.sendingRequest(true));
  try {
    const response = yield call(auth.getBankList);
    return response.data;
  } catch (error) {
    yield put(action.requestError(error));
  } finally {
    yield put(action.sendingRequest(false));
  }
}

export function* bankListFlow() {
  while (true) {
    yield take(BANK_LIST_REQUEST);
    const response = yield call(fetchBankList);
    yield put(action.changeBankListData(response));
  }
}

export function* kybBusinessDataFlow() {
  while (true) {
    const request = yield take(GET_DROPDOWN_REQUEST);
    const { city, kecamatan, province } = request.payload;
    const provinceResponse = yield call(fetchBusinessProvince);
    const newProvinceList = {};
    for (const item of provinceResponse) {
      newProvinceList[item.name] = item.id;
    }
    yield put(action.changeBusinessProvinceData(newProvinceList));
    if (province) {
      const provinceId = newProvinceList[province];
      if (provinceId) {
        const cityResponse = yield call(fetchBusinessCity, provinceId);
        const newCityList = {};
        for (const item of cityResponse) {
          newCityList[item.name] = item.id;
        }
        yield put(action.changeBusinessCityData(newCityList));
        const cityId = newCityList[city];
        if (cityId) {
          const kecamatanResponse = yield call(fetchBusinessKecamatan, cityId);
          const newKecamatanList = {};
          for (const item of kecamatanResponse) {
            newKecamatanList[item.name] = item.id;
          }
          yield put(action.changeBusinessKecamatanData(newKecamatanList));
          const kecamatanId = newKecamatanList[kecamatan];
          if (kecamatanId) {
            const zipcodeResponse = yield call(
              fetchBusinessZipcode,
              kecamatanId
            );
            yield put(action.changeBusinessZipcodeData(zipcodeResponse));
          }
        }
      }
    }
  }
}

export function* businessSalesFlow() {
  while (true) {
    yield take(BUSINESS_SALES_REQUEST);
    const salesState = [
      { id: 5000000, name: '< 5 million IDR' },
      { id: 10000000, name: '5-10 million IDR' },
      { id: 20000000, name: '10-20 million IDR' },
      { id: 100000000, name: '20-100 million IDR' },
      { id: 999999999, name: '> 100 million IDR' },
    ];
    yield put(action.changeBusinessSalesData(salesState));
  }
}

export function* businessInfo() {
  while (true) {
    const request = yield take(SET_BUSINESS_INFORMATION);
    yield put(action.sendingRequest(true));
    yield put(action.showProgressBar());
    const { data } = request.payload.businessInfo;
    const { name, ...businessInfoData } = data;

    try {
      const response = yield call(
        auth.setBusinessInformationOnBusinessService,
        businessInfoData
      );
      if (response.status === 200) {
        yield call(updateBusiness, { name });
        const profile = yield call(getProfile);
        const profileData = profile.data;
        yield put(action.setAuthState(true, profileData));

        clevertapRegister.clevertapTracker(
          'Registration Business Information Completed',
          {
            user_id: profileData.id,
            activeSubscription: !profileData.business.is_trial,
            isBusinessOwner: profileData.is_owner,
            email: profileData.email,
            phone: profileData.phone,
            businessCategory: data.business_category_id,
            numberOfOutlets: data.number_of_outlet_option,
            cityLocation: data.city,
          }
        );
        forwardTo('/kyb');
      }
    } catch (error) {
      assertErrorHasMessage(error);
      yield put(actionNotification.error(error.message));
    } finally {
      yield put(action.hideProgressBar());
      yield put(action.sendingRequest(false));
    }
  }
}

export function* redirectTo() {
  while (true) {
    const request = yield take(REDIRECT_TO);
    forwardTo(`/${request.payload.url}`);
  }
}

export function* loadResetPassword() {
  while (true) {
    const request = yield take(LOAD_RESET_PASSWORD);
    try {
      const response = yield call(auth.getResetPassword, {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        access_token: request.payload.token,
      });
      const user = JSON.parse(response.data.user);
      yield put(
        action.changeFormResetPassword({
          tokenError: false,
          fullName: `${user.first_name} ${user.last_name}`,
        })
      );
    } catch (error) {
      assertErrorHasData(error);
      if (error.status === 404) {
        yield put(
          action.changeFormResetPassword({
            tokenError: true,
            message: error.data.message,
          })
        );
      }
    }
  }
}

export function* sendResetPassword() {
  while (true) {
    const requestPayload = yield take(SEND_RESET_PASSWORD);
    const responsePayload = requestPayload.payload.data;
    responsePayload.access_token = requestPayload.payload.access_token;
    yield put(action.sendingRequest(true));
    yield put(action.showProgressBar());
    try {
      yield call(auth.resetPassword, responsePayload);
      yield put(forgotPasswordAction.passwordChangeBy('phone'));
      forwardTo('/success-forgot-password');
    } catch (error) {
      yield put(action.requestError(error));
    } finally {
      yield put(action.hideProgressBar());
      yield put(action.sendingRequest(false));
    }
  }
}

export function* loadPrepareEncryption() {
  while (true) {
    yield take(LOAD_PREPARE_ENCRYPTION);
    try {
      const response = yield call(auth.loadPrepareEncryption);
      const responseData = response.data;
      yield put(action.setPrepareEncryption({ responseData }));
    } catch (e) {
      yield put(action.requestError(e));
    }
  }
}

export function* getProfileFlow() {
  while (true) {
    yield take(GET_PROFILE_REQUEST);
    const { data } = yield call(apiGetProfile, {});
    if (data) {
      yield put(action.setProfile(data));
    }
  }
}

export function* toLoginPage() {
  while (true) {
    yield take(TO_LOGIN_PAGE);
    forwardTo('/login');
  }
}

// The root saga is what we actually send to Redux's middleware. In here we fork
// each saga so that they are all "active" and listening.
// Sagas are fired once at the start of an app and can be thought of as processes running
// in the background, watching actions dispatched to the store.
export default function* root() {
  yield fork(loginFlow);
  yield fork(logoutFlow);
  yield fork(businessTypesFlow);
  yield fork(resendVerificationEmailFlow);
  yield fork(businessCategoriesAllFlow);
  yield fork(businessStartsFlow);
  yield fork(businessLocationsFlow);
  yield fork(businessSalesFlow);
  yield fork(businessCategoriesFlow);
  yield fork(redirectTo);
  yield fork(loadResetPassword);
  yield fork(sendResetPassword);
  yield fork(loadPrepareEncryption);
  yield fork(getProfileFlow);
  yield fork(toLoginPage);
  yield fork(businessInfo);
  yield fork(businessCityFlow);
  yield fork(businessNumberOfOutletsFlow);
  yield fork(businessCitiesAllFlow);
  yield fork(businessCitiesPopularFlow);
  yield fork(businessProvinceFlow);
  yield fork(businessZipcodeFlow);
  yield fork(businessKecamatanFlow);
  yield fork(bankListFlow);
  yield fork(kybBusinessDataFlow);
  yield fork(businessOpeningChoicesFlow);
  yield fork(businessInformationFlow);
}
